import type { CSSProperties } from "react";

/**
 * The landing page's icon set.
 *
 * 🔴 ONE STROKE SET, 2 px ROUND CAPS ON A 24 px GRID — `design-system.md` §6.
 * The previous page drew these at 2.2, 2.5 and 3 px depending on which one you
 * looked at, which is what makes a row of icons read as clip-art rather than as
 * a set. New icons here are 2 px; the four the hero already renders keep their
 * original weights, because the hero is not being redesigned and a stroke change
 * would be a design change.
 *
 * ⚠️ THESE ARE UI ICONS, AND THE PRODUCT'S UI ICONS ARE NEVER EMOJI (§6). Emoji
 * are game identity only — the library tile, the HUD, the parent report — which
 * is why the game cards on this page carry artwork and a skill-coloured dot
 * rather than 🔢 at 64 px.
 *
 * Every icon takes `className` (Tailwind sizes and colours it via
 * `currentColor`) and `style`, so a caller can hand one a `--kq-delay` and stop
 * two sparkles twinkling in lockstep.
 */

export interface IconProps {
  className?: string;
  style?: CSSProperties;
}

/* ────────────────────────────────────────────── HERO — kept verbatim ── */

export function UserAvatarIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );
}

export function ArrowRightIcon({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
      />
    </svg>
  );
}

export function CheckIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

/* `style` is accepted so callers can hand it a `--kq-delay` and desynchronise
   two sparkles that would otherwise twinkle in lockstep. */
export function SparkleIcon({ className = "h-6 w-6", style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" />
    </svg>
  );
}

/* ──────────────────────────────────────── THE SET — 2 px, 24 px grid ── */

/** The shared wrapper. Every icon below is one stroke weight, by construction. */
function Stroke({
  className = "h-5 w-5",
  style,
  d,
}: IconProps & { d: string[] }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {d.map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}

/** A small tick, for a list of facts. Lighter than `CheckIcon`'s 3 px hero weight. */
export function TickIcon(props: IconProps) {
  return <Stroke {...props} d={["M5 12.5l4.5 4.5L19 7"]} />;
}

export function ChevronRightIcon(props: IconProps) {
  return <Stroke {...props} d={["M9 5l7 7-7 7"]} />;
}

export function ArrowUpIcon(props: IconProps) {
  return <Stroke {...props} d={["M12 20V4", "M5.5 10.5L12 4l6.5 6.5"]} />;
}

/** Down, for the hero's "the games" link — the section is below, so the arrow is. */
export function ArrowDownIcon(props: IconProps) {
  return <Stroke {...props} d={["M12 4v16", "M18.5 13.5L12 20l-6.5-6.5"]} />;
}

export function MenuIcon(props: IconProps) {
  return <Stroke {...props} d={["M4 7h16", "M4 12h16", "M4 17h16"]} />;
}

export function CloseIcon(props: IconProps) {
  return <Stroke {...props} d={["M6 6l12 12", "M18 6L6 18"]} />;
}

/** Camera — the panel the child sees, and the thing this product is about. */
export function CameraIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M3 8.5A2.5 2.5 0 015.5 6h1.7a1 1 0 00.83-.45l.94-1.4A1 1 0 019.8 3.7h4.4a1 1 0 01.83.45l.94 1.4a1 1 0 00.83.45h1.7A2.5 2.5 0 0121 8.5v8A2.5 2.5 0 0118.5 19h-13A2.5 2.5 0 013 16.5v-8z",
        "M15 12.2a3 3 0 11-6 0 3 3 0 016 0z",
      ]}
    />
  );
}

/** Privacy. The lock is the product's single most important promise. */
export function LockIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M7 10.5V7.5a5 5 0 0110 0v3",
        "M5.5 10.5h13A1.5 1.5 0 0120 12v7a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 19v-7a1.5 1.5 0 011.5-1.5z",
      ]}
    />
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M12 3l7.5 3v5.4c0 4.6-3 8.3-7.5 9.6-4.5-1.3-7.5-5-7.5-9.6V6L12 3z",
        "M9 12.3l2.1 2.1L15.3 10",
      ]}
    />
  );
}

/** A laptop — "on this device", and the only hardware a school needs. */
export function DeviceIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M5 5.5h14a1 1 0 011 1V16H4V6.5a1 1 0 011-1z",
        "M2.5 19h19",
        "M10 16h4",
      ]}
    />
  );
}

/** The school building, from the shared set — a stack, not a cartoon. */
export function SchoolIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M12 3.5L21 8l-9 4.5L3 8l9-4.5z",
        "M6 10v5.6c0 .5.3 1 .8 1.2A11 11 0 0012 18a11 11 0 005.2-1.2c.5-.2.8-.7.8-1.2V10",
      ]}
    />
  );
}

/** Reports — the bar chart a school buys, drawn as a chart and not as paper. */
export function ReportsIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M4 20h16", "M7.5 20v-6", "M12 20V6", "M16.5 20v-9"]}
    />
  );
}

/** Children — two figures, one shorter, at the size the set is drawn. */
export function ChildrenIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M9 6.5a2 2 0 11-4 0 2 2 0 014 0z",
        "M3.5 20v-3.5A2.5 2.5 0 016 14h2a2.5 2.5 0 012.5 2.5V20",
        "M18.5 8.5a1.75 1.75 0 11-3.5 0 1.75 1.75 0 013.5 0z",
        "M13.8 20v-2.8a2.2 2.2 0 012.2-2.2h1.5a2.2 2.2 0 012.2 2.2V20",
      ]}
    />
  );
}

/** The body in motion — a running figure, for the whole-body claim. */
export function BodyIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M15.5 5.2a1.6 1.6 0 11-3.2 0 1.6 1.6 0 013.2 0z",
        "M13.6 9.2L10 11.4l1.6 3.1L9 20",
        "M13.6 9.2l3.1 1.6 1.4 3.4",
        "M11.6 14.5l3.9.6 1.3 4.9",
        "M9.6 10.6L6 10",
      ]}
    />
  );
}

/** Voice — pre-recorded, and the interface for a child who cannot read. */
export function VoiceIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M12 4a2.8 2.8 0 012.8 2.8v5a2.8 2.8 0 11-5.6 0v-5A2.8 2.8 0 0112 4z",
        "M6 11.4a6 6 0 0012 0",
        "M12 17.4V20",
      ]}
    />
  );
}

/** Offline — the outbox that makes a round survive a dead school network. */
export function OfflineIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M3 4l18 16",
        "M5.2 9.6a11 11 0 013.4-2.1",
        "M15.6 7.9a11 11 0 013.2 1.7",
        "M8.3 13.2a6.6 6.6 0 012-1.1",
        "M14 12.4a6.6 6.6 0 011.7 1",
        "M12 17.8h.01",
      ]}
    />
  );
}

/** Curriculum coverage — the grid from the shared set. */
export function GridIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M4 4.5h6.2v6.2H4V4.5z",
        "M13.8 4.5H20v6.2h-6.2V4.5z",
        "M4 13.8h6.2V20H4v-6.2z",
        "M13.8 13.8H20V20h-6.2v-6.2z",
      ]}
    />
  );
}

/** Target — alignment to a named standard, not a vague "aligned" claim. */
export function TargetIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
        "M16 12a4 4 0 11-8 0 4 4 0 018 0z",
        "M12 12h.01",
      ]}
    />
  );
}

/**
 * An open book — "curriculum aligned", drawn as the thing a curriculum lives in.
 *
 * ⚠️ IT IS A STROKE ICON, NOT THE 📚 THE COMP SUGGESTS. §6 allows emoji for game
 * identity only; a trust strip is UI, and four emoji in a row on a page a school
 * reads during procurement is the exact register the adult tier avoids.
 */
export function BookIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M12 7.2C10.6 5.9 8.7 5.2 6.5 5.2H4v12h2.5c2.2 0 4.1.7 5.5 2",
        "M12 7.2c1.4-1.3 3.3-2 5.5-2H20v12h-2.5c-2.2 0-4.1.7-5.5 2",
        "M12 7.2v12",
      ]}
    />
  );
}

/**
 * A star, for the "games played" tile.
 *
 * ⚠️ FILLED, NOT STROKED, AND IT IS ONE OF ONLY TWO FILLED GLYPHS IN THE SET. A
 * stroked five-point star at 16 px turns to mush — its interior angles land
 * inside two pixels of each other — which is why every icon set that has one
 * fills it.
 */
export function StarIcon({ className = "h-4 w-4", style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 3.2l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.4l5.9-.9L12 3.2z" />
    </svg>
  );
}

/** A clock, for the "average time per week" tile. */
export function ClockIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M21 12a9 9 0 11-18 0 9 9 0 0118 0z", "M12 7.5V12l3 1.8"]}
    />
  );
}

/* ──────────────────────────────────── WEBSITE — VIDEO, FAQ, BREADCRUMB ── */

/**
 * Play, for "play free" and "watch a round".
 *
 * ⚠️ FILLED, LIKE THE STAR. A stroked triangle at 14 px reads as a chevron.
 */
export function PlayIcon({ className = "h-4 w-4", style }: IconProps) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.1v13.8a1 1 0 001.5.86l11-6.9a1 1 0 000-1.72l-11-6.9A1 1 0 008 5.1z" />
    </svg>
  );
}

/** Pause, for the background-video control. Filled, to pair with `PlayIcon`. */
export function PauseIcon({ className = "h-4 w-4", style }: IconProps) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="5" width="4" height="14" rx="1.2" />
      <rect x="14" y="5" width="4" height="14" rx="1.2" />
    </svg>
  );
}

export function PlusIcon(props: IconProps) {
  return <Stroke {...props} d={["M12 5v14", "M5 12h14"]} />;
}

export function MinusIcon(props: IconProps) {
  return <Stroke {...props} d={["M5 12h14"]} />;
}

export function ArrowLeftIcon(props: IconProps) {
  return <Stroke {...props} d={["M19 12H5", "M11 18l-6-6 6-6"]} />;
}

/** An "i" in a circle — the grown-ups' note on a game page. */
export function InfoIcon(props: IconProps) {
  return <Stroke {...props} d={["M21 12a9 9 0 11-18 0 9 9 0 0118 0z", "M12 11v5.5", "M12 7.6h.01"]} />;
}

/** Layers — "three levels". */
export function LayersIcon(props: IconProps) {
  return <Stroke {...props} d={["M12 3l9 4.5-9 4.5-9-4.5L12 3z", "M3 12l9 4.5 9-4.5", "M3 16.5L12 21l9-4.5"]} />;
}

/** Video with a line through it — "nothing is recorded". */
export function VideoOffIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M15 15.5V17a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 013 17V8.5A1.5 1.5 0 014.5 7H6", "M10 7h3.5A1.5 1.5 0 0115 8.5v2l6-3.5v10", "M3 3l18 18"]}
    />
  );
}

/** A cloud with a line through it — "nothing is uploaded". */
export function CloudOffIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M8.5 6.2A6 6 0 0117.7 10H18a4 4 0 012.4 7.2", "M17 19H7.5a4.5 4.5 0 01-1.3-8.8", "M3 3l18 18"]}
    />
  );
}

/* ─────────────────────────────────── GAME PAGES — WHAT A CHILD DOES ── */

/** Hash — "find the right number". */
export function CountIcon(props: IconProps) {
  return <Stroke {...props} d={["M4 9h16", "M4 15h16", "M10 3L8 21", "M16 3l-2 18"]} />;
}

/** Arrows both ways — step or lean. */
export function LeanIcon(props: IconProps) {
  return <Stroke {...props} d={["M8 7l-5 5 5 5", "M16 7l5 5-5 5", "M3 12h18"]} />;
}

/** An open hand — hold still, or point with an open palm. */
export function HandIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M18 11V6a2 2 0 00-4 0v5",
        "M14 10V4a2 2 0 00-4 0v6",
        "M10 10.5V6a2 2 0 00-4 0v8",
        "M18 8a2 2 0 114 0v6a8 8 0 01-8 8h-2c-2.8 0-4.5-.9-6-2.4l-3.6-3.6a2 2 0 012.8-2.8L7 15",
      ]}
    />
  );
}

export function JumpIcon(props: IconProps) {
  return <Stroke {...props} d={["M12 3v13", "M6 9l6-6 6 6", "M5 21h14"]} />;
}

export function DuckIcon(props: IconProps) {
  return <Stroke {...props} d={["M12 3v13", "M6 10l6 6 6-6", "M5 21h14"]} />;
}

export function EarIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M6 8.5a6.5 6.5 0 1113 0c0 6-6 6-6 10a3.5 3.5 0 11-7 0", "M15 8.5a2.5 2.5 0 00-5 0v1a2 2 0 110 4"]}
    />
  );
}

export function FootprintsIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M4 16v-2.4C4 11.5 3 10.5 3 8c0-2.7 1.5-6 4.5-6C9.4 2 11 3.8 11 6.5c0 2.5-1 3.5-1 5.6V16z",
        "M20 20v-2.4c0-2.1 1-3.1 1-5.6 0-2.7-1.5-6-4.5-6C14.6 6 13 7.8 13 10.5c0 2.5 1 3.5 1 5.6V20z",
        "M16 17h4",
        "M4 13h4",
      ]}
    />
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z", "M15 12a3 3 0 11-6 0 3 3 0 016 0z"]}
    />
  );
}

/** A figure with both arms up — copy a pose, raise both arms. */
export function PoseIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M14 4.5a2 2 0 11-4 0 2 2 0 014 0z", "M12 8v6", "M12 14l-3 7", "M12 14l3 7", "M4 4l8 6 8-6"]}
    />
  );
}

export function RunIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={["M17 4a2 2 0 11-4 0 2 2 0 014 0z", "M7 21l3-6 3 2v5", "M5 12l4-3 4 1 3 4h4", "M10 15l2-5"]}
    />
  );
}

/** A pointing hand. */
export function PointIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M10 9.5V4a2 2 0 00-4 0v10",
        "M14 10V9a2 2 0 00-4 0v1",
        "M18 11v-1a2 2 0 00-4 0",
        "M18 11a2 2 0 114 0v3a8 8 0 01-8 8h-2c-2.8 0-4.5-.9-6-2.3l-3.6-3.6a2 2 0 012.8-2.8L7 15",
      ]}
    />
  );
}

/** A closing fist — grab. */
export function GrabIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M18 11.5V9a2 2 0 00-4 0v1.4",
        "M14 10V8a2 2 0 00-4 0v2",
        "M10 9.9V9a2 2 0 00-4 0v5",
        "M6 14a2 2 0 00-4 0",
        "M18 11a2 2 0 114 0v3a8 8 0 01-8 8h-4a8 8 0 01-8-8 2 2 0 114 0",
      ]}
    />
  );
}

/** A piece dropping onto a bridge. */
export function DropIcon(props: IconProps) {
  return <Stroke {...props} d={["M3 10h18", "M6 10v9", "M18 10v9", "M3 19h18", "M12 2v5", "M9 5l3 3 3-3"]} />;
}

/* ──────────────────────────────────────────── FOOTER — GET IN TOUCH ── */

/**
 * The three contact glyphs.
 *
 * ⚠️ DRAWN, NOT `material-icons-round`. The reference footer these come from
 * pulls a Google icon font for three glyphs — a render-blocking stylesheet, a
 * webfont download, and a flash of ligature text ("email", "phone",
 * "location_on") in the reader's face before it loads. Three inline paths cost
 * nothing, sit on the same 2 px / 24 px grid §6 sets for the rest of this file,
 * and inherit `currentColor` so the footer colours them with a Tailwind class.
 */
export function MailIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M3 7.5A1.5 1.5 0 014.5 6h15A1.5 1.5 0 0121 7.5v9a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 16.5v-9z",
        "M3.5 7.8l8.5 5.4 8.5-5.4",
      ]}
    />
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M7.2 3.6h3l1.5 3.8-2 1.4a11 11 0 005.5 5.5l1.4-2 3.8 1.5v3a1.8 1.8 0 01-2 1.8A16.5 16.5 0 015.4 5.6a1.8 1.8 0 011.8-2z",
      ]}
    />
  );
}

export function LocationIcon(props: IconProps) {
  return (
    <Stroke
      {...props}
      d={[
        "M19.5 10.5c0 5.6-7.5 10.5-7.5 10.5S4.5 16.1 4.5 10.5a7.5 7.5 0 1115 0z",
        "M14.5 10.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z",
      ]}
    />
  );
}

/* ───────────────────────────────────── FOOTER — WHERE TO FIND KALQY ── */

/**
 * The three channel marks.
 *
 * 🔴 THESE BREAK §6's STROKE RULE ON PURPOSE, AND THEY ARE THE ONLY ICONS IN
 * THIS FILE THAT MAY. That rule governs icons *this product draws*; these are
 * three other companies' registered trademarks, and a brand mark redrawn as a
 * 2 px outline is not that brand's mark any more — it is a lookalike, which is
 * both wrong to the eye and the exact thing every one of those three brands'
 * guidelines forbids. All three are the official solid glyph, verbatim.
 *
 * ⚠️ `fill="currentColor"`, SO THEY STILL TAKE THE FOOTER'S COLOUR. Reproducing
 * the *shape* faithfully is the obligation; reproducing LinkedIn blue,
 * Instagram's gradient and YouTube red is not, and three loud foreign colours in
 * a block whose whole job is to be quiet would wreck it. A monochrome mark is
 * explicitly permitted by all three brands.
 */
export function LinkedInIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
    </svg>
  );
}

export function InstagramIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

export function YouTubeIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
